import {
  Box,
  Button,
  Divider,
  Icons,
  Menu,
  MenuItem,
  Radio,
  Tooltip,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import React, { useMemo, useState } from 'react';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import { createStyles } from './GroupFilter.styles';

export const MANAGE_GROUPS_VALUE = 'manage-groups';
export const CLEAR_GROUP_FILTER_VALUE = 'footer-actions';
const MAX_DROPDOWN_HEIGHT = 320;

export type GroupFilterSet = {
  externalId?: string;
  isHeader?: boolean;
  key: string;
  label: string;
};

type GroupFilterProps = {
  groups: GroupFilterSet[];
  onClear: () => void;
  onManageGroups: () => void;
  onSelectGroup: (groupId: string) => void;
  value: string;
};

const GroupLabel = ({
  children,
  sx,
}: {
  children: string;
  sx?: Record<string, unknown>;
}) => {
  const [overflowRef, isOverflowing] = useIsOverflowing<HTMLSpanElement>();

  return (
    <Tooltip
      arrow
      disableFocusListener={!isOverflowing}
      disableHoverListener={!isOverflowing}
      disableTouchListener={!isOverflowing}
      position="top"
      title={children}
    >
      <Box
        component="span"
        ref={overflowRef}
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...sx,
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

const GroupFilter = ({
  groups,
  onClear,
  onManageGroups,
  onSelectGroup,
  value,
}: GroupFilterProps) => {
  const theme = useTheme();
  const isDark = theme?.palette?.mode === 'dark';
  const actionColor = isDark
    ? theme?.palette?.primary?.light
    : theme?.palette?.primary?.dark;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuMaxHeight, setMenuMaxHeight] = useState(480);
  const options = useMemo(() => {
    const seen = new Set<string>();
    return groups.filter(({ externalId, isHeader, key }) => {
      if (isHeader) return !externalId;
      if (
        externalId ||
        key === MANAGE_GROUPS_VALUE ||
        key === CLEAR_GROUP_FILTER_VALUE
      ) {
        return false;
      }
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [groups]);

  const selectedLabel =
    options.find(({ key }) => key === value)?.label || 'Filter by group';
  const closeMenu = () => setAnchorEl(null);
  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { bottom } = event.currentTarget.getBoundingClientRect();
    // Keep the list below its trigger. Overflowing groups scroll within the menu
    // instead of causing MUI to reposition the menu over the trigger.
    setMenuMaxHeight(Math.max(160, window.innerHeight - bottom - 16));
    setAnchorEl(event.currentTarget);
  };
  const selectGroup = (groupId: string) => {
    closeMenu();
    onSelectGroup(groupId);
  };
  const groupListMaxHeight = Math.max(
    80,
    Math.min(MAX_DROPDOWN_HEIGHT, menuMaxHeight) - 104
  );
  const styles = createStyles(theme, isDark, actionColor, groupListMaxHeight);

  return (
    <>
      <Button
        aria-label="Filter submissions by group"
        aria-controls={anchorEl ? 'group-filter-menu' : undefined}
        aria-expanded={Boolean(anchorEl)}
        aria-haspopup="menu"
        endIcon={
          anchorEl ? <Icons.KeyboardArrowUp /> : <Icons.KeyboardArrowDown />
        }
        kind="secondary"
        onClick={openMenu}
        sx={styles.filterButton}
      >
        <GroupLabel sx={styles.selectedLabel}>{selectedLabel}</GroupLabel>
      </Button>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        compact
        id="group-filter-menu"
        onClose={closeMenu}
        open={Boolean(anchorEl)}
        PaperProps={{
          sx: styles.menuPaper,
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        <Box sx={styles.groupList}>
          {options.map(({ isHeader, key, label }) =>
            isHeader ? (
              <Box key={key} sx={styles.groupSet}>
                <GroupLabel sx={styles.groupSetText}>{label}</GroupLabel>
              </Box>
            ) : (
              <MenuItem
                className="group-filter-option"
                key={key}
                aria-checked={key === value}
                label={
                  <Box
                    alignItems="center"
                    display="flex"
                    gap={1}
                    sx={styles.groupOptionLabel}
                  >
                    <Radio
                      checked={key === value}
                      inputProps={{ 'aria-hidden': true }}
                      onChange={() => {}}
                      tabIndex={-1}
                      value={key}
                    />
                    <GroupLabel sx={styles.groupOptionText}>{label}</GroupLabel>
                  </Box>
                }
                onClick={() => selectGroup(key)}
                role="menuitemradio"
                selected={key === value}
              />
            )
          )}
        </Box>
        <Divider />
        <Box sx={styles.footer}>
          <MenuItem
            icon={<Icons.SettingsOutlined fontSize="small" />}
            label={
              <Typography sx={styles.manageText}>Manage groups</Typography>
            }
            onClick={() => {
              closeMenu();
              onManageGroups();
            }}
          />

          <Box sx={styles.clearContainer}>
            <MenuItem
              label={<Typography sx={styles.clearText}>Clear</Typography>}
              onClick={() => {
                closeMenu();
                onClear();
              }}
            />
          </Box>
        </Box>
      </Menu>
    </>
  );
};

export default GroupFilter;
